"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BreadcrumbsWidget = void 0;
var tslib_1 = require("tslib");
var MoreDotsIcon_1 = require("@tableau/icons/MoreDotsIcon");
var React = tslib_1.__importStar(require("react"));
var TypeStyle = tslib_1.__importStar(require("typestyle"));
var Localize_1 = require("../../../gen-src/Localize");
var Standard_1 = require("../../styles/Standard");
var Declarative_1 = require("../../utils/Declarative");
var DomUtil_1 = require("../../utils/DomUtil");
var ReactHelpers_1 = require("../../utils/ReactHelpers");
var StackingComponent_1 = require("../../utils/StackingComponent");
var MenuButtonWidget_1 = require("../Button/MenuButtonWidget");
var DropdownOverlay_1 = require("../Dropdown/DropdownOverlay");
var TextLinkWidget_1 = require("../TextLinkWidget");
var BreadcrumbsStyles_1 = require("./BreadcrumbsStyles");
/** @returns the index of the first breadcrumb that is not collapsed */
function determineVisibleBreadcrumbs(navWidth, collapseStartIndex, breadcrumbWidths, extraWidths) {
    var widthBeforeOverflow = 0;
    for (var i = 0; i < collapseStartIndex; i++) {
        widthBeforeOverflow += breadcrumbWidths[i];
    }
    var extraWidth = extraWidths.reduce(function (a, b) { return a + b; }, 0);
    var collapsibleSpace = navWidth - widthBeforeOverflow - extraWidth;
    var collapsibleSpaceWithMenu = collapsibleSpace - (BreadcrumbsStyles_1.BreadcrumbsMenuButtonMinWidth + BreadcrumbsStyles_1.BreadcrumbDividerWidth + BreadcrumbsStyles_1.BreadcrumbSpacing * 2);
    var breadcrumbsTotalWidth = 0;
    var firstVisibleBreadcrumb = collapseStartIndex;
    // To determine how many breadcrumbs should be visible, loop through the list of
    // breadcrumbs backwards and compare the length of adding the next breadcrumb to
    // the overall available space.
    for (var i = breadcrumbWidths.length - 1; i >= collapseStartIndex; i--) {
        breadcrumbsTotalWidth += breadcrumbWidths[i];
        // if the first collapsible breadcrumb is not visible, we show the overflow menu button,
        // which takes up some of the available space
        var breadcrumbsGreaterThanAvailableSpace = i === collapseStartIndex ?
            (breadcrumbsTotalWidth > collapsibleSpace) :
            (breadcrumbsTotalWidth > collapsibleSpaceWithMenu);
        if (breadcrumbsGreaterThanAvailableSpace) {
            firstVisibleBreadcrumb = i + 1;
            break;
        }
    }
    return firstVisibleBreadcrumb;
}
/**
 * `BreadcrumbsWidget` is a `nav` region for displaying `TextLinkWidget`s (and/or a `string` to represent the current position) to represent a navigation tree.
 * When there is not enough horizontal space to display all of the breadcrumbs, the breadcrumbs will collapse into an overflow menu.
 */
var BreadcrumbsWidget = function (props) {
    var ariaLabel = props["aria-label"], ariaLabelledby = props["aria-labelledby"], children = props.children, collapseStartIndex = props.collapseStartIndex, darkBackground = props.darkBackground, testId = props.testId, trailingElements = props.trailingElements, breadcrumbsProps = tslib_1.__rest(props, ['aria-label', 'aria-labelledby', "children", "collapseStartIndex", "darkBackground", "testId", "trailingElements"]);
    var _a = React.useState(collapseStartIndex), firstVisibleBreadcrumbIndex = _a[0], setFirstVisibleBreadcrumbIndex = _a[1];
    var _b = React.useState(null), navEl = _b[0], setNavEl = _b[1];
    var _c = React.useState(null), divEl = _c[0], setDivEl = _c[1];
    var _d = React.useState(0), updateCount = _d[0], setUpdateCount = _d[1];
    var breadcrumbWidths = React.useRef([]);
    var extraWidths = React.useRef([]);
    if (!(ariaLabel || ariaLabelledby)) {
        throw new Error('At least one of `aria-label` or `aria-labelledby` must be set on `breadcrumbsProps` props.');
    }
    // reset everything any time the list changes
    React.useLayoutEffect(function () {
        setFirstVisibleBreadcrumbIndex(collapseStartIndex);
        setUpdateCount(function (count) { return count + 1; }); // aka forceUpdate
    }, [children, collapseStartIndex, trailingElements]);
    // determine the width of each breadcrumb before rendering, so that the number of visible breadcrumbs
    // can be calculated based on how many breadcrumbs fit within the width of the container
    React.useLayoutEffect(function () {
        if (!divEl || !navEl) {
            return;
        }
        var crumbs = Array.from(navEl.querySelectorAll('li'));
        breadcrumbWidths.current = crumbs.map(function (crumb, index) { var _a; return (_a = crumb.offsetWidth) !== null && _a !== void 0 ? _a : breadcrumbWidths.current[index]; });
        var extras = Array.from(divEl.children).slice(1);
        extraWidths.current = extras.map(function (extraElement, index) {
            var _a;
            return (_a = extraElement.clientWidth) !== null && _a !== void 0 ? _a : extraWidths.current[index];
        });
        setFirstVisibleBreadcrumbIndex(determineVisibleBreadcrumbs(divEl.clientWidth, collapseStartIndex, breadcrumbWidths.current, extraWidths.current));
    }, [collapseStartIndex, divEl, navEl, updateCount]);
    var onResize = function (entries) {
        var navWidth = entries[0].contentRect.width;
        if (navWidth > 0) {
            setFirstVisibleBreadcrumbIndex(determineVisibleBreadcrumbs(navWidth, collapseStartIndex, breadcrumbWidths.current, extraWidths.current));
        }
    };
    ReactHelpers_1.useResizeObserver(divEl, onResize);
    var parentBreadcrumbs = children.slice(0, collapseStartIndex);
    var collapsedBreadcrumbs = children.slice(collapseStartIndex, firstVisibleBreadcrumbIndex);
    var visibleBreadcrumbs = children.slice(firstVisibleBreadcrumbIndex);
    return React.createElement("div", tslib_1.__assign({ className: BreadcrumbsStyles_1.BreadcrumbsDivStyleName, "data-tb-test-id": Declarative_1.makeTestId(testId, 'BreadcrumbsWidget'), ref: setDivEl }, breadcrumbsProps),
        React.createElement("nav", { "aria-label": ariaLabel, "aria-labelledby": ariaLabelledby, className: BreadcrumbsStyles_1.BreadcrumbsNavStyleName, ref: setNavEl },
            React.createElement("ol", { className: BreadcrumbsStyles_1.BreadcrumbsListStyleName },
                (collapseStartIndex > 0) && parentBreadcrumbs.map(function (breadcrumb, index) {
                    return React.createElement(Breadcrumb, { key: index, breadcrumb: breadcrumb, index: index, breadcrumbsLength: children.length, darkBackground: darkBackground });
                }),
                (firstVisibleBreadcrumbIndex !== collapseStartIndex) &&
                    React.createElement("li", { style: { position: 'relative' } },
                        React.createElement(BreadcrumbsOverflow, { children: collapsedBreadcrumbs, kind: darkBackground ? 'outlineDark' : 'outline', testId: testId }),
                        firstVisibleBreadcrumbIndex <= children.length - 1 && React.createElement(BreadcrumbDivider, { darkBackground: darkBackground })),
                visibleBreadcrumbs.map(function (breadcrumb, index) {
                    return React.createElement(Breadcrumb, { breadcrumb: breadcrumb, key: index + firstVisibleBreadcrumbIndex, index: index + firstVisibleBreadcrumbIndex, breadcrumbsLength: children.length, darkBackground: darkBackground });
                }))),
        React.createElement("div", { className: BreadcrumbsStyles_1.TrailingElementsContainerStyleName }, trailingElements));
};
exports.BreadcrumbsWidget = BreadcrumbsWidget;
exports.BreadcrumbsWidget.displayName = 'BreadcrumbsWidget';
exports.BreadcrumbsWidget.defaultProps = { collapseStartIndex: 0 };
var BreadcrumbDivider = function (props) {
    var darkBackground = props.darkBackground;
    return React.createElement("span", { "aria-hidden": true, className: TypeStyle.classes(BreadcrumbsStyles_1.BreadcrumbDividerStyleName, { darkBackground: darkBackground }) }, '/');
};
BreadcrumbDivider.displayName = 'BreadcrumbDivider';
var Breadcrumb = React.forwardRef(function (props, breadcrumbRef) {
    var breadcrumb = props.breadcrumb, index = props.index, breadcrumbsLength = props.breadcrumbsLength, darkBackground = props.darkBackground;
    var crumb = breadcrumb;
    if (typeof breadcrumb !== 'string') {
        crumb = React.cloneElement(breadcrumb, {
            kind: darkBackground ? TextLinkWidget_1.TextLinkKind.StandaloneDark : TextLinkWidget_1.TextLinkKind.Standalone,
        });
    }
    return React.createElement("li", { className: TypeStyle.classes(BreadcrumbsStyles_1.BreadcrumbStyleName, { darkBackground: darkBackground }), key: index, ref: breadcrumbRef },
        crumb,
        index < breadcrumbsLength - 1 && React.createElement(BreadcrumbDivider, { darkBackground: darkBackground }));
});
Breadcrumb.displayName = 'Breadcrumb';
var BreadcrumbsOverflow = function (props) {
    var children = props.children, kind = props.kind, testId = props.testId;
    var _a = React.useState(false), isOpen = _a[0], setOpen = _a[1];
    var _b = React.useState(null), buttonElement = _b[0], setButtonElement = _b[1];
    var _c = React.useState(null), overflowElement = _c[0], setOverflowElement = _c[1];
    var stackingRootRef = React.useRef(null);
    var mouseDownOnButton = React.useRef(false);
    var overflowMenuId = React.useState(DomUtil_1.generateId())[0];
    var breadcrumbs = children.map(function (breadcrumb, index) {
        var crumb = breadcrumb;
        if (typeof breadcrumb !== 'string') {
            // with `darkBackground`, the `TextLinkWidget`s would be `TextLinkKind.StandaloneDark`
            // but need to be `TextLinkKind.Standalone` to work with the white background of the overflow menu
            crumb = React.cloneElement(breadcrumb, {
                kind: TextLinkWidget_1.TextLinkKind.Standalone,
            });
        }
        return React.createElement("li", { key: index }, crumb);
    });
    var handleOverflowClick = function (e) {
        var target = e.target;
        // clicking on one of the links in the overflow should close the overflow
        if (target.tagName === 'A') {
            toggleOverflow(false);
        }
    };
    var toggleOverflow = function (open) {
        if (open === void 0) { open = !isOpen; }
        if (open === isOpen) {
            return;
        }
        setOpen(open);
    };
    var handleBlur = function (e) {
        var toElement = e.relatedTarget;
        if (!toElement || e.currentTarget.contains(toElement)) {
            return;
        }
        if (!mouseDownOnButton.current) {
            toggleOverflow(false);
        }
        mouseDownOnButton.current = false;
    };
    var handleKeyDown = function (e) {
        if (e.key === 'Escape' && !e.repeat) {
            toggleOverflow(false);
            buttonElement === null || buttonElement === void 0 ? void 0 : buttonElement.focus();
            e.preventDefault();
        }
        e.stopPropagation();
    };
    var handleButtonMouseDown = function () {
        mouseDownOnButton.current = true;
    };
    var handleButtonMouseUp = function () {
        mouseDownOnButton.current = false;
    };
    var handleButtonClick = function (e) {
        if (e.currentTarget.contains(e.target)) {
            toggleOverflow();
        }
    };
    ReactHelpers_1.useGlobalPointerDownOutsideListener(buttonElement, overflowElement, function () {
        mouseDownOnButton.current = false;
        toggleOverflow(false);
    });
    return React.createElement(React.Fragment, null,
        React.createElement(MenuButtonWidget_1.MenuButtonWidget, { "aria-controls": overflowMenuId, "aria-haspopup": null, "aria-label": Localize_1.Messages.moreBreadcrumbsButton(), className: BreadcrumbsStyles_1.BreadcrumbMenuButtonStyleName, children: React.createElement(MoreDotsIcon_1.MoreDotsIcon, { "aria-hidden": true, size: 12, style: { verticalAlign: 'bottom' } }), density: 'high', isOpen: isOpen, kind: kind, onClick: handleButtonClick, onMouseDown: handleButtonMouseDown, onMouseUp: handleButtonMouseUp, ref: setButtonElement, style: { minWidth: BreadcrumbsStyles_1.BreadcrumbsMenuButtonMinWidth }, testId: Declarative_1.makeTestId(testId, 'BreadcrumbsOverflow') }),
        React.createElement("div", { style: { position: 'absolute' }, ref: stackingRootRef }),
        isOpen &&
            React.createElement(StackingComponent_1.StackingRootContext.Provider, { value: stackingRootRef.current },
                React.createElement(DropdownOverlay_1.DropdownOverlay, { alignment: 'left' // by default, will still switch right if out of space
                    , getAnchorClientRect: function () { return buttonElement.getBoundingClientRect(); }, zIndex: 1 },
                    React.createElement("div", { id: overflowMenuId, onBlur: handleBlur, onClick: handleOverflowClick, onKeyDown: handleKeyDown, ref: setOverflowElement, style: Standard_1.PopupStyle },
                        React.createElement("ol", { "aria-label": Localize_1.Messages.moreBreadcrumbs(), className: BreadcrumbsStyles_1.BreadcrumbsOverflowListStyleName }, breadcrumbs)))));
};
BreadcrumbsOverflow.displayName = 'BreadcrumbsOverflow';
//# sourceMappingURL=BreadcrumbsWidget.js.map